// src/components/Modals/uploadHandlers.js
import { getDocumentTypes, uploadDocuments } from '../../api/api';

export const MAX_FILE_COUNT = 5;
export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25MB
export const MAX_NOTE_LENGTH = 256;

export const bytesToKB = (bytes) => Math.ceil(bytes / 1024);

export const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const fetchDocumentTypes = async (t, setDocType, setErrorMessages) => {
  try {
    const apiData = await getDocumentTypes();
    if (apiData?.attachedDocumentTypes) {
      const keyNames = apiData.attachedDocumentTypes.map(item => item.keyName);
      setDocType(keyNames);
    }
  } catch (error) {
    console.error('Error fetching document types:', error);
    setErrorMessages(prev => [...prev, t('uploadDocs.fetchTypeError')]);
  }
};

export const handleFileSelection = async ({
  files,
  selectedFiles,
  accountID,
  t,
  setErrorMessages,
  setSelectedFiles
}) => {
  try {
    if (files.length + selectedFiles.length > MAX_FILE_COUNT) {
      setErrorMessages(prev => [
        ...prev,
        t('uploadDocs.tooManyFiles', { maxCount: MAX_FILE_COUNT })
      ]);
      return;
    }

    const processedFiles = await Promise.all(
      files.map(async (file) => {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          setErrorMessages(prev => [
            ...prev,
            t('uploadDocs.fileSizeExceeded', {
              fileName: file.name,
              maxSize: MAX_FILE_SIZE_BYTES / (1024 * 1024)
            })
          ]);
          return null;
        }

        const base64Data = await readFileAsBase64(file);
        return {
          fileName: file.name,
          data64: base64Data,
          accountID,
          note: "",
          docType: "",
          source: "BrokerPortal",
          fileSize: bytesToKB(file.size)
        };
      })
    );

    const validFiles = processedFiles.filter(Boolean);
    setSelectedFiles(prev => [...prev, ...validFiles]);
  } catch (error) {
    console.error('Error processing files:', error);
    setErrorMessages(prev => [...prev, t('uploadDocs.processError')]);
  }
};

export const handleDocumentUpload = async ({
  selectedFiles,
  t,
  setErrorMessages,
  setUploading,
  setUploadStats,
  setSuccess,
  setAllUploadsComplete
}) => {
  if (!selectedFiles.length) {
    setErrorMessages([t('uploadDocs.noFilesSelected')]);
    return;
  }

  // Validate document types
  const invalidFiles = selectedFiles.filter(file => !file.docType);
  if (invalidFiles.length) {
    setErrorMessages(prev => [...prev, t('uploadDocs.selectDocType')]);
    return;
  }

  setErrorMessages([]);
  setUploading(true);
  setUploadStats({ success: 0, failed: 0 });

  try {
    for (let i = 0; i < selectedFiles.length; i++) {
      const document = selectedFiles[i];
      
      setSuccess(prev => ({
        ...prev,
        [i]: { neutral: true, yes: false, no: false }
      }));

      try {
        const result = await uploadDocuments(document);
        if (result?.isSuccess) {
          setUploadStats(prev => ({ ...prev, success: prev.success + 1 }));
          setSuccess(prev => ({
            ...prev,
            [i]: { neutral: false, yes: true, no: false }
          }));
        } else {
          setUploadStats(prev => ({ ...prev, failed: prev.failed + 1 }));
          setSuccess(prev => ({
            ...prev,
            [i]: { neutral: false, yes: false, no: true }
          }));
          setErrorMessages(prev => [
            ...prev,
            result.message || t('uploadDocs.uploadFailed', { fileName: document.fileName })
          ]);
        }
      } catch (error) {
        setUploadStats(prev => ({ ...prev, failed: prev.failed + 1 }));
        setSuccess(prev => ({
          ...prev,
          [i]: { neutral: false, yes: false, no: true }
        }));
        setErrorMessages(prev => [...prev, error.message]);
      }
    }
  } finally {
    setUploading(false);
    setAllUploadsComplete(true);
  }
};

export const handleFileInputChange = async ({
  event,
  uploading,
  selectedFiles,
  accountID,
  t,
  setErrorMessages,
  setSelectedFiles
}) => {
  if (uploading) return;

  const files = Array.from(event.target.files || []);
  await handleFileSelection({
    files,
    selectedFiles,
    accountID,
    t,
    setErrorMessages,
    setSelectedFiles
  });
};